/**
 * PortalCurrencyContext: USD/UZS toggle for the dealer/usta portal.
 * Exchange rate defaults to 12300 UZS/USD.
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

const CURRENCY_KEY = "wst_portal_currency";
const RATE_KEY = "wst_portal_rate";
const DEFAULT_RATE = 12300.0;

interface PortalCurrencyContextValue {
  currency: "USD" | "UZS";
  rate: number;
  toggle: () => void;
  setCurrency: (c: "USD" | "UZS") => void;
  formatPrice: (usdValue: number | string | null | undefined) => string;
}

const PortalCurrencyContext = createContext<PortalCurrencyContextValue | null>(null);

export function PortalCurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currency, setCurrencyState] = useState<"USD" | "UZS">(() => {
    try {
      const stored = localStorage.getItem(CURRENCY_KEY);
      return stored === "UZS" ? "UZS" : "USD";
    } catch {
      return "USD";
    }
  });

  const [rate, setRate] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(RATE_KEY);
      return stored ? parseFloat(stored) : DEFAULT_RATE;
    } catch {
      return DEFAULT_RATE;
    }
  });

  const toggle = useCallback(() => {
    setCurrencyState(prev => {
      const next = prev === "USD" ? "UZS" : "USD";
      try { localStorage.setItem(CURRENCY_KEY, next); } catch { /* noop */ }
      return next;
    });
  }, []);

  const setCurrency = useCallback((c: "USD" | "UZS") => {
    setCurrencyState(c);
    try { localStorage.setItem(CURRENCY_KEY, c); } catch { /* noop */ }
  }, []);

  const formatPrice = useCallback(
    (usdValue: number | string | null | undefined): string => {
      if (usdValue == null || usdValue === "") return "—";
      const n = typeof usdValue === "string" ? parseFloat(usdValue) : usdValue;
      if (isNaN(n)) return "—";
      if (currency === "UZS") {
        const uzs = n * rate;
        return uzs.toLocaleString("uz-Cyrl", { maximumFractionDigits: 0 }) + " UZS";
      }
      return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    [currency, rate]
  );

  const value = useMemo(
    () => ({ currency, rate, toggle, setCurrency, formatPrice }),
    [currency, rate, toggle, setCurrency, formatPrice]
  );

  return <PortalCurrencyContext.Provider value={value}>{children}</PortalCurrencyContext.Provider>;
}

export function usePortalCurrency(): PortalCurrencyContextValue {
  const ctx = useContext(PortalCurrencyContext);
  if (!ctx) throw new Error("usePortalCurrency must be used inside PortalCurrencyProvider");
  return ctx;
}
